import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import { getCurrentPage, getHeader } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getWidgetsByPageId } from "@insite/client-framework/Store/Data/Widgets/WidgetSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Menu from "@insite/mobius/Icons/Menu";
import { getProp } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import SignOut from "@store/Components/Icons/SignOut";
import AccountListMenu from "@store/Components/Store/MyAccount/AccountListMenu";
import setIsMenuOpen from "@store/Store/Pages/MyAccount/Handlers/SetIsMenuOpen";
import StoreApplicationState from "@store/Store/StoreApplicationState";
import { sideMenuLinkStyles } from "@store/theme/common/mixins";
import { TYPOGRAPHY } from "@store/theme/design";
import { AccountListModel } from "@store/Widgets/LoginButton";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: StoreApplicationState) => {
    const myAccountState = state.store.pages.myAccount;

    const sideColumnRowWidget = getWidgetsByPageId(state, getCurrentPage(state).id).find((widget: WidgetProps) => {
        return widget.type === "SideColumnRow";
    });
    const loginButtonWidget = getWidgetsByPageId(state, getHeader(state).id).find((widget: WidgetProps) => {
        return widget.type === "LoginButton";
    });

    const accountMenuLinks = loginButtonWidget?.fields.links as AccountListModel[] ?? [];

    return {
        accountMenuLinks,
        isMenuOpen: myAccountState.isMenuOpen,
        isMenuOverlay: sideColumnRowWidget?.fields.isOverlay,
    }
}

const mapDispatchToProps = {
    signOut,
    setIsMenuOpen,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

interface SideMenuStyles {
    wrapper: InjectableCss;
    container: InjectableCss;
    menuButton: ButtonPresentationProps;
    signOutButton: ButtonPresentationProps;
    menu: InjectableCss;
    signOutLink: ButtonPresentationProps;
    signOutIcon: IconPresentationProps;
}

const _iconHeight = 50;

const buttonStyles = `
        min-width: 100%;
        min-height: ${_iconHeight}px;
        padding: 0;

        &:focus {
            outline: 0;
        }
`;

const sideMenuStyles: SideMenuStyles = {
    wrapper: {
        css: css`
            position: relative;
            width: 50px;
        `
    },
    container: {
        css: css`
            display: flex;
            flex-direction: column;
            justify-content: space-between;
        	background-color: ${getColor("common.accent")};
        `
    },
    menuButton: {
        shape: "rectangle",
        buttonType: undefined,
        css: css`
            ${buttonStyles}
            background-color: transparent;
            border: 0;
            color: ${getColor("text.main")};

            &:hover {
                color: ${getColor("text.accent")};
            }
        `
    },
    signOutButton: {
        shape: "rectangle",
        css: css`
            ${buttonStyles}

            ${ButtonIcon} {
                top: auto;
                width: 1.5em;
                height: 1.5em;

                svg {
                    width: inherit;
                    height: inherit;
                }
            }
        `
    },
    menu: {
        css: css`
            position: absolute;
            top: 0;
            left: 3em;
            width: 320px;
            z-index: ${getProp("theme.zIndex.popover")};
            background-color: ${getColor("common.accent")};
            box-shadow: ${getProp("theme.shadows.lightDrop")};
        `
    },
    signOutLink: {
        shape: "rectangle",
        css: css`
            ${sideMenuLinkStyles("0.3em")}
            display: flex;
            align-items: center;
            position: absolute;
            bottom: 0;
            right: 0;
            padding-left: 60px;
            width: 368px;
            height: ${_iconHeight}px;
            font-weight: ${TYPOGRAPHY.weight.bold};
        `,
    },
    signOutIcon: {
        src: SignOut,
        size: 20,
        css: css`
            position: relative;
            z-index: ${getProp("theme.zIndex.popoverIcon")};
        `
    }
}

const styles = sideMenuStyles;

const SideMenu: React.FC<Props> = ({
    accountMenuLinks,
    isMenuOpen,
    isMenuOverlay,
    signOut,
    setIsMenuOpen,
}) => {
    const [menuHeight, setMenuHeight] = useState<number>(0);
    const menuRef = useRef<HTMLDivElement | null>(null);

    useLayoutEffect(() => {
        calculateMenuHeight();
    }, [accountMenuLinks]);

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);

        return () => {
          document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleClickOutside = (event: MouseEvent) => {
        // Do not close menu if it's not in overlay
        if (isMenuOverlay && menuRef.current && !menuRef.current.contains(event.target as Node)) {
            setIsMenuOpen(false);
        }
    };

    const calculateMenuHeight = () => {
        const padding = 15;
        const fallbackHeight = 600;
        let height = 0;

        accountMenuLinks.forEach((fields) => {
            switch(fields.fields.isCategoryTitle) {
                case "Category":
                    height += 50;
                    break;
                case "Link":
                    height += 30;
                    break;
            }
        });

        height > 0
            ? setMenuHeight(height + _iconHeight + padding)
            : setMenuHeight(fallbackHeight + _iconHeight + padding);
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper
                {...styles.container}
                style={{ height: `${menuHeight}px` }}
            >
                <Button
                    {...styles.menuButton}
                    onClick={() => setIsMenuOpen(!isMenuOpen)}
                    aria-label={translate("Open Menu")}
                >
                    <ButtonIcon src={Menu} />
                </Button>
                <Button
                    {...styles.signOutButton}
                    onClick={() => signOut()}
                    aria-label={translate("Sign Out")}
                >
                    <Icon {...styles.signOutIcon} />
                </Button>
            </StyledWrapper>
            {isMenuOpen && (
                <StyledWrapper
                    {...styles.menu}
                    style={{ height: `${menuHeight}px` }}
                    ref={menuRef}
                >
                    <AccountListMenu />
                    <Button
                        {...styles.signOutLink}
                        onClick={() => signOut()}
                        aria-label={translate("Sign Out")}
                    >
                        {translate("Sign Out")}
                    </Button>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(SideMenu),
    definition: {
        group: "Basic",
        icon: "link-simple",
        fieldDefinitions: [],
    },
};

export default widgetModule;
